import { A } from '@ember/array';
import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { ERROR_MESSAGES } from "../../constants/error-messages";

/**
 * The API enums for the user/report endpoint
 *
 * @type {Object}
 */
export const REPORT_USER_API_REASONS = {
  OFFENSIVE_LANGUAGE: 'offensive_language',
  OFFSITE_PAYMENT: 'offsite_payment',
  OTHER: 'other'
};

/**
 * Service for application related API calls.
 */
export default Service.extend({
  /**
   * Register services
   */
  threadManager: service('managers/thread-manager'),
  errorTracker: service('error-tracker'),
  session: service(),
  shData: service(),
  api: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Add a new message to a thread.
   *
   * @param {Thread} thread
   * @param {String} message
   * @param {String[]} assetIDs
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  addNewMessageToThread(thread, message, assetIDs, opt_includes = []) {
    return new Promise((resolve, reject) => {
      this._addMessage(thread, message, assetIDs, opt_includes)
        .then(message => resolve(message))
        .catch(e => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error saving your message. Please refresh the page and try again.',
            'addNewMessageToThread -- error saving message',
          );

          reject(e);
        });
    });
  },

  /**
   * Load a thread with given includes.
   *
   * @param {Thread} thread
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadThread(thread, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);
    return this.api.get(`threads/${thread.id}/`, options);
  },

  /**
   * report a given threadId
   *
   * @param {String} threadId
   * @param {String} reason       of type REPORT_USER_API_REASONS.*
   * @param {String} message
   * @param {Boolean=} opt_isBlock
   * @return {Ember.RSVP.Promise}
   */
  reportThread(threadId, reason, message, opt_isBlock) {
    let options = {
      jsonData: {
        reason: reason,
        message: message,
        block: opt_isBlock
      },
    };

    return this.get('api').post(`threads/${threadId}/report`, options)
      .catch(e => {
        this.get('errorTracker').error(`reportThread -- error reporting thread id ${threadId}`, this, e);
        throw e;
      });

  },

  /**
   * Send a message to a list of specific threadIds
   *
   * @param {String} message
   * @param {Ember.MutableArray<Number>} threadIds
   */
  sendMessageToThreadIds(message, threadIds) {
    return this._sendMassMessage(threadIds, message);
  },

  /**
   * Send a new message to a user
   *
   * @param {User} targetUser
   * @param {String} subject
   * @param {String} message
   * @param {String[]} assetIDs
   */
  sendNewMessage(targetUser, subject, message, assetIDs) {
    let threadManager = this.get('threadManager'),
      store = this.get('store'),
      currentUser = this.get('currentUser'),
      recipient = targetUser,
      users = [currentUser, recipient],
      createdAt = new Date();

    let newThread = store.createRecord('thread', {
      unread: false,
      name: subject,
      users: users,
      createdAt: createdAt,
      recipient: recipient
    });

    let savedThread = null;

    return newThread.save()
      .then((thread) => {
        savedThread = thread;

        // Add the users to the thread
        let userIdsAndRoles = A();

        savedThread.get('users').forEach(user => {
          let role;

          if (user.get('id') === currentUser.get('id')) {
            role = (currentUser.get('isBuyer')) ? 'buyer' : 'seller';
          } else {
            role = (currentUser.get('isBuyer')) ? 'seller' : 'buyer';
          }

          userIdsAndRoles.pushObject({
            id: user.get('id'),
            role: role
          });
        });

        return savedThread.addUsers(threadManager.buildAddRemoveUserData(userIdsAndRoles));
      })
      .then(() => {
        const includes = [
          'user',
          'assets.uri-collection',
        ];

        return this._addMessage(savedThread, message, assetIDs, includes);
      })
      .then(() => {
        return savedThread;
      }).catch((error) => {
        this.get('errorTracker')
          .error(
            `sendNewMessage -- addingUser or savingMessage -- from currentUser ${this.get('currentUser.id')} to targetUser ${targetUser.get('id')}`,
            this,
            error,
          );
        throw new Error(error);
      });
  },

  /**
   * Add a new thread given the recipient and thread title
   *
   * @param {User} userRecipient
   * @param {String} threadTitle
   * @private
   */
  _addNewThread(userRecipient, threadTitle) {
    let newThread = this.get('store').createRecord('thread', {
      users: [this.get('currentUser'), userRecipient],
      unread: false,
      createdAt: new Date(),
      recipient: userRecipient,
      name: threadTitle
    });

    return newThread.save().catch((e) => {
      this.get('errorTracker').error('_addNewThread - ' + e.toString(), this);
    });
  },

  /**
   * Add a message to a given thread.
   *
   * @param {Thread} thread
   * @param {String} message
   * @param {String[]} assetIDs
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   * @private
   */
  _addMessage(thread, message, assetIDs, opt_includes = []) {
    const jsonData = {
      user_id: this.get('currentUser.id'),
      body: message,
      thread_id: thread.get('id'),
    };

    if (assetIDs.length) {
      jsonData.related_asset_ids = assetIDs;
    }

    const options = this.shData.addIncludesToOptions({ jsonData }, opt_includes);

    return this.get('api').post('messages/', options)
      .then((response) => {
        this.shData.pushResponseToStore(response);

        const message = this.get('store').peekRecord('message', response.data.id);
        message.set('thread', thread);
        return message;
      })
      .catch((error) => {
        this.get('errorTracker').error('_addMessage - failed', this, error);
        throw error;
      });
  },

  /**
   * Load a user by id from the store or the db.
   *
   * @param {Number} userId
   * @return {Promise<User>}
   * @private
   */
  _loadUser(userId) {
    let user = this.get('store').peekRecord('user', userId);
    if (user) {
      return Promise.resolve(user);
    }

    return this.get('store').findRecord('user', userId).catch(e => {
      this.get('errorTracker').error('_loadUser - ' + userId, this);
    });
  },

  /**
   * load a thread or create a new one with the given recipient.
   *
   * @param {User} userRecipient
   * @param {Thread|Number|undefined} opt_thread  Thread or the thread Id
   * @param {String} opt_threadTitle
   * @return {Promise<Thread>}
   * @private
   */
  _loadThread(userRecipient, opt_thread, opt_threadTitle) {
    let thread = opt_thread,
      threadTitle = opt_threadTitle;

    if (thread) {
      if (typeof thread === "number") {
        let threadId = thread;

        //try to get it out of the local store
        thread = this.get('store').peekRecord('thread', threadId);

        if (!thread) {
          //not there, let's load from db.
          return this.get('store').findRecord('thread', threadId).catch(e => {
            this.get('errorTracker').error('_loadThread - ' + threadId, this);
          });
        }

        return Promise.resolve(thread);
      }

      //thread is a normal object, load the store's version
      thread = this.get('store').peekRecord('thread', thread);
      return Promise.resolve(thread);
    }

    //at this point we don't have a thread, so we need to create a new one.
    return new Promise((resolve, reject) => {
      this._addNewThread(userRecipient, threadTitle).then((newThread) => {
        resolve(newThread);
      }).catch(e => {
        this.get('errorTracker').error('_loadThread - ' + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Send a message to a number of threadIds
   *
   * @param {Array<Number>} threadIds
   * @param {String} message
   * @private
   */
  _sendMassMessage(threadIds, message) {
    let options = {
      jsonData: {
        thread_ids: threadIds,
        user_id: this.get('currentUser.id'),
        body: message
      },
      params : {
        include: 'thread'
      }
    };

    return this.get('api').post('messages/mass-message', options).then((response) => {
      let date = new Date();

      this.shData.pushResponseToStore(response);

      threadIds.forEach(threadId => {
        let thread = this.get('store').peekRecord('thread', threadId);
        thread.set('updatedAt', date);

        let applications = thread.get('applications');
        applications.forEach(application => {
          application.set('updatedAt', date);

          let currentUser = this.get('currentUser');
          if (currentUser.get('isBuyer')) {
            // We know a buyer has sent a message, so we can go ahead and clear the expiresAt flag since we know
            // the buyer has a sent a message now, and it can't expire.
            application.set('expiresAt', null);
          }

          let appSummary = this.get('store').peekRecord('applicationSummary', application.get('id'));
          if (appSummary) {
            appSummary.set('updatedAt', date);
          }
        });

      });
    }).catch(e => this.get('errorTracker').error('_sendMassMessage - ' + threadIds.toString(), this));
  },

  /**
   * Array of user ids to send this message to
   *
   * NOTE - Function not tested, and I believe the logic is wrong as well.
   *
   * @param {Array<Number>} userId
   * @param {String} message
   * @param {Thread|Number|undefined} opt_thread  Thread or the thread Id
   * @param {String} opt_threadTitle
   * @private
   * @return {Promise<Message>}
   */
  _sendMessageToUser(userId, message, opt_thread, opt_threadTitle) {
    return new Promise((resolve, reject) => {
      this._loadUser(userId).then(userRecipient => {
        this._loadThread(userRecipient, opt_thread, opt_threadTitle).then(loadedThread => {
          this._addMessage(loadedThread, message).then((newMessage => {
            resolve(newMessage);
          })).catch(e => {
            this.get('errorTracker').error('_sendMessageToUser message - ' + e.toString(), this);
            reject(e);
          });
        }).catch(e => {
          this.get('errorTracker').error('_sendMessageToUser - thread' + e.toString(), this);
          reject(e);
        });
      }).catch(e => {
        this.get('errorTracker').error('_sendMessageToUser - user' + e.toString(), this);
        reject(e);
      });
    });
  }
});
